// CameraController
// Features:
// - Manages main camera controls (Orbit vs PointerLock) based on phase
// - Handles switching between Intro, Travel, and In-Cabin states
// - Orchestrates camera transitions via child components

import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { OrbitControls, PointerLockControls } from '@react-three/drei'
import * as THREE from 'three'

import IntroTravel from './IntroTravel'
import CabinTravel from './CabinTravel'
import Parallax from './Parallax'

import { usePhase, PHASES } from '../../store/usePhase'
import { useStore } from '../../store/useStore'
import { useActions, OVERLAY_STATE } from '../../store/useActions'

const CONFIG = {
    introPosition: new THREE.Vector3(12, 25, 18),
    introTarget: new THREE.Vector3(-6.893, 14, 2.757),
    unlockLookDistance: 10,
}

export default function CameraController() {
    const controlsRef = useRef()
    const camera = useThree((state) => state.camera)

    const phase = usePhase((state) => state.phase)
    const setPointerLocked = useStore((state) => state.setPointerLocked)
    const overlayState = useActions((state) => state.overlayState)

    // Initial Camera Setup
    useEffect(() => {
        if (!camera) return
        camera.position.copy(CONFIG.introPosition)
        if (controlsRef.current) {
            controlsRef.current.target.copy(CONFIG.introTarget)
            controlsRef.current.update()
        }
    }, [camera])

    // Control Logic
    const isOrbitEnabled = phase !== PHASES.inCabin
    const isPlcEnabled =
        phase === PHASES.inCabin && overlayState === OVERLAY_STATE.transparent

    // Sync pointer lock state
    useEffect(() => {
        if (!isPlcEnabled) setPointerLocked(false)
    }, [isPlcEnabled, setPointerLocked])

    // Handle unlock: Maintain orientation by syncing Orbit target
    const handleUnlock = () => {
        setPointerLocked(false)

        if (controlsRef.current && camera) {
            const dir = new THREE.Vector3()
            camera.getWorldDirection(dir)

            controlsRef.current.target
                .copy(camera.position)
                .add(dir.multiplyScalar(CONFIG.unlockLookDistance))

            controlsRef.current.update()
        }
    }

    return (
        <>
            {/* Orbit: Used for transitions and intro, disabled in-cabin */}
            <OrbitControls
                ref={controlsRef}
                enabled={isOrbitEnabled}
                enablePan={false}
                enableZoom={false}
                enableRotate={false}
            />

            {/* PointerLock: Active only in-cabin when overlay is clear */}
            {isPlcEnabled && (
                <PointerLockControls
                    enabled={true}
                    onLock={() => setPointerLocked(true)}
                    onUnlock={handleUnlock}
                />
            )}

            {/* State-specific Behaviors */}
            <IntroTravel controlsRef={controlsRef} />
            <CabinTravel controlsRef={controlsRef} />
            <Parallax controlsRef={controlsRef} />
        </>
    )
}
